// TEA delivers CCMR data as a "CCMR Verifier" workbook beginning with 2026 accountability:
// a Glossary tab plus one tab per campus holding one row per student. Recognized by the
// student header row on any campus tab.

// cell.text throws on merged cells whose master value is null, so derive text from
// cell.value instead.
const cellText = (cell) => {
  const value = cell ? cell.value : null;
  if (value == null) {
    return '';
  }
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join('');
    }
    if (value.text != null) {
      return String(value.text);
    }
    return '';
  }
  return String(value);
};

// Match on the verifier's structural signature, not just a few generic CCMR columns: a tab named
// by a 9-digit campus number carrying the full header set. 'Continuously Enrolled…' and 'Military'
// are verifier-only — they are absent from the CCMR Tracker and CCMR Outcomes Bonus exports — so
// requiring them (plus the per-campus tab name) keeps those easily-confused files from matching here.
const RequiredHeaders = [
  'District #',
  'Campus #',
  'Student Name',
  'CCMR Met',
  'Excluded from Accountability',
  'Continuously Enrolled in District in the Past 4 Years',
  'Military',
];

const isCampusTabName = (name) => /^\d{9}$/.test((name || '').trim());

const isXlsxCcmrVerifier = (file) =>
  new Promise((resolve) => {
    const isXlsxFile =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      (file.name || '').toLowerCase().endsWith('.xlsx');
    if (!isXlsxFile) {
      resolve({ isValid: false });
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve({ isValid: false });
    reader.onload = async () => {
      try {
        const exceljsModule = await import('exceljs/dist/exceljs.min.js');
        const ExcelJS = exceljsModule.default || exceljsModule;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(reader.result);
        const hasCampusTab = workbook.worksheets.some((sheet) => {
          if (!isCampusTabName(sheet.name)) {
            return false;
          }
          const headerRow = sheet.getRow(1);
          const headers = [];
          for (let col = 1; col <= sheet.columnCount; col += 1) {
            headers.push(cellText(headerRow.getCell(col)).trim());
          }
          return RequiredHeaders.every((header) => headers.includes(header));
        });
        resolve({ isValid: hasCampusTab });
      } catch (error) {
        resolve({ isValid: false });
      }
    };
    reader.readAsArrayBuffer(file);
  });

export default isXlsxCcmrVerifier;
